import { z } from 'zod';

/** sessionStorage key holding the in-flight cross-team preview switch. */
export const PREVIEW_SWITCH_STATE_KEY = 'previewTeamSwitchReturnPath';

/** Redirects allowed per switch. Bounds the loop; the second attempt covers a flaky Kinde return. */
export const MAX_SWITCH_ATTEMPTS = 2;

const previewSwitchStateSchema = z.object({
  /** Preview path + query (minus `teamId`) the switch must land back on. */
  returnPath: z.string(),
  /** Team the preview belongs to. The switch has not taken until this is the connected team. */
  targetTeamId: z.string(),
  /** Redirects issued so far, including the one in flight. */
  attempts: z.number(),
  /** Set once the root provider has bounced to `returnPath`, so it bounces at most once per attempt. */
  bounced: z.boolean(),
});

export type PreviewSwitchState = z.infer<typeof previewSwitchStateSchema>;

/** The address a stash is matched against. A stash applies only to the path it was written for. */
export function currentPath(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
}

/** Unreadable values read as no switch. That covers malformed JSON and any older stash shape. */
export function readPreviewSwitchState(): PreviewSwitchState | null {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(PREVIEW_SWITCH_STATE_KEY);
  if (!raw) return null;

  try {
    const parsed = previewSwitchStateSchema.safeParse(JSON.parse(raw));
    if (parsed.success) return parsed.data;
  } catch {
    // fall through
  }

  window.sessionStorage.removeItem(PREVIEW_SWITCH_STATE_KEY);
  return null;
}

export function writePreviewSwitchState(state: PreviewSwitchState): void {
  window.sessionStorage.setItem(PREVIEW_SWITCH_STATE_KEY, JSON.stringify(state));
}

export function clearPreviewSwitchState(): void {
  window.sessionStorage.removeItem(PREVIEW_SWITCH_STATE_KEY);
}
